import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Badge, Empty } from 'antd';
import { useAuth } from '../../../../providers/AuthProvider';
import Opportunity from '../../../../models/Opportunity';
import TalentPortal from '../../../talent-portal/TalentPortal';
import LoadingSpinner from '../../../../components/LoadingSpinner';
import DashboardCard, { LastUpdatedPill } from './DashboardCard';

/**
 * Projects card for the new talent dashboard.
 * Functional / Tailwind port of ProjectPanel + OpportunityListView.
 */

const ALL_TAB = 'All';

const daysSince = (date) => {
    if (!date || !TB.moment(date).isValid()) return null;
    return Math.max(0, TB.moment().diff(TB.moment(date), 'days'));
};

// Map Opportunity.statusColor() onto pill styles
const statusPillClasses = (opportunity) => {
    switch (opportunity.statusColor()) {
        case TB.config('color.purple'):
            return 'bg-primary/10 text-primary';
        case TB.config('color.green'):
            return 'bg-green-50 text-green-700';
        case TB.config('color.black'):
            return 'bg-slate-800 text-white';
        default: // lost / declined / on hold
            return 'bg-gray-100 text-gray-500';
    }
};

const Pill = ({ className, children }) => (
    <span className={`inline-flex shrink-0 items-center rounded-md px-2 py-0.5 text-xxs font-bold uppercase tracking-wider ${className}`}>
        {children}
    </span>
);

const OpportunityLink = ({ to, count, children }) => (
    <Link to={to} className="inline-flex items-center gap-1 text-xs font-semibold text-primary hover:text-primary-dark hover:no-underline">
        {!!count && <Badge count={count} size="small" />}
        {children}
    </Link>
);

const OpportunityItem = ({ opportunity, auth }) => {
    const isAvailable = opportunity.isAvailable();
    const canEngage = opportunity.canMessageAndAcceptInterviews();

    return (
        <div
            className={TB.mapClass('rounded-xl border border-solid border-gray-200 bg-white p-3 hover:border-primary/30 sm:p-4', {
                'pointer-events-none opacity-60': !isAvailable,
            })}
        >
            <Link
                to={opportunity.detailLink()}
                title={opportunity.project?.name}
                className="block truncate text-sm font-semibold leading-snug text-gray-900 hover:text-primary hover:no-underline"
            >
                {opportunity.project?.name}
            </Link>

            <div className="mt-1.5 flex flex-wrap items-center gap-x-2 gap-y-1">
                <Pill className={statusPillClasses(opportunity)}>{opportunity.display_status}</Pill>
                {opportunity.hasUnseenUpdate() && (
                    <Pill className="bg-amber-100 text-amber-700">Updated</Pill>
                )}
                {opportunity.last_updated && (
                    <span className="text-xs text-gray-500">
                        Updated {TB.moment(opportunity.last_updated).fromNow()}
                    </span>
                )}
            </div>

            {canEngage && (auth.canViewTalentOppMessages() || auth.canViewTalentOppInterviews()) && (
                <div className="mt-2 flex flex-wrap gap-4">
                    {auth.canViewTalentOppMessages() && (
                        <OpportunityLink
                            to={{ pathname: opportunity.proposalLink(), search: '?tabView=messages' }}
                            count={opportunity.unread_messages_count}
                        >
                            Messages
                        </OpportunityLink>
                    )}
                    {auth.canViewTalentOppInterviews() && (
                        <OpportunityLink
                            to={{ pathname: opportunity.proposalLink(), search: '?tabView=interviews' }}
                            count={opportunity.proposed_meetings_count}
                        >
                            Interviews
                        </OpportunityLink>
                    )}
                </div>
            )}
        </div>
    );
};

const OpportunityList = ({ opportunities }) => {
    const { auth } = useAuth();
    const [activeTab, setActiveTab] = useState(ALL_TAB);

    // Same grouping as OpportunityListView: newest first, one tab per display status
    const tabs = useMemo(() => {
        let opps = TB.map(Opportunity.makeMany(opportunities || []), opportunity => {
            opportunity.display_status = opportunity.displayStatus();
            opportunity.last_updated = opportunity.lastUpdated();
            return opportunity;
        });
        opps = TB.orderBy(opps, 'last_updated', 'desc');

        const grouped = TB.groupBy(opps, 'display_status');

        return TB.reduce(Opportunity.statusOptions(), (result, status) => {
            result[status] = grouped[status] || [];
            return result;
        }, { [ALL_TAB]: opps });
    }, [opportunities]);

    const visibleOpps = tabs[activeTab] || [];

    return (
        <div className="flex flex-col gap-4">
            <div className="flex flex-wrap gap-2" role="tablist" aria-label="Project status">
                {TB.keys(tabs).map(status => (
                    <button
                        key={status}
                        type="button"
                        role="tab"
                        aria-selected={status === activeTab}
                        onClick={() => setActiveTab(status)}
                        className={TB.mapClass('rounded-full border-0 px-3 py-1 text-xs font-semibold', {
                            'bg-primary text-white': status === activeTab,
                            'bg-gray-100 text-gray-600 hover:bg-gray-200': status !== activeTab,
                        })}
                    >
                        {status}
                    </button>
                ))}
            </div>

            {visibleOpps.length ? (
                <div className="-mr-1 flex max-h-[300px] flex-col gap-3 overflow-y-auto pr-1">
                    {visibleOpps.map(opportunity => (
                        <OpportunityItem key={`opp-${opportunity.id}`} opportunity={opportunity} auth={auth} />
                    ))}
                </div>
            ) : (
                <Empty
                    className="m-0 py-6"
                    image={Empty.PRESENTED_IMAGE_SIMPLE}
                    description={<span className="text-sm text-gray-500">{`No Projects ${activeTab} Yet`}</span>}
                />
            )}
        </div>
    );
};

const ProjectsCard = ({
    opportunities,
    totalUpdates,
    lastViewed,
    className,
    title,
    disabledReason,
}) => {
    const { auth } = useAuth();

    return (
        <DashboardCard
            title={title || TB.tryTrans('talent.dashboard.project_title') || 'Projects'}
            count={totalUpdates}
            className={className}
            disabled={!auth?.isProfileApproved?.()}
            disabledReason={disabledReason}
            headerRight={<LastUpdatedPill prefix="" days={daysSince(lastViewed)} />}
            footer={(
                <Link
                    to={TalentPortal.projectsListPath()}
                    className="ml-auto inline-flex items-center justify-center whitespace-nowrap rounded-lg bg-primary px-5 py-2.5 text-xs font-semibold text-white hover:bg-primary-dark hover:text-white hover:no-underline"
                >
                    View all projects
                </Link>
            )}
        >
            {opportunities ? (
                <OpportunityList opportunities={opportunities} />
            ) : (
                <div className="flex min-h-[160px] items-center justify-center">
                    <LoadingSpinner size="lg" />
                </div>
            )}
        </DashboardCard>
    );
};

ProjectsCard.propTypes = {
    opportunities: PropTypes.array,
    totalUpdates: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
    lastViewed: PropTypes.any,
    className: PropTypes.string,
    title: PropTypes.node,
    disabledReason: PropTypes.node,
};

export default ProjectsCard;
